<script setup lang="ts">
import { zenDesktStyle } from '@/shared/zenDesk';
import { useModalStore } from '@/stores/apostatudo/modal';
import { useStoriesStore } from '@/stores/apostatudo/stories';
import ApStories from '@/components/apostatudo/organisms/Stories.vue';
import { useStories } from '@/composables/apostatudo/use-stories';

// Use SSR-safe IDs for Headless UI
provideHeadlessUseId(() => useId());

const route = useRoute();

// home-bg precisa estar no HTML inicial do SSR para o gradient aparecer na primeira carga.
// index.vue fica dentro de <ClientOnly>, então seu useHead não roda no servidor —
// por isso centralizamos aqui, onde o setup() executa tanto no server quanto no client.
useHead(
  computed(() =>
    route.name === 'index' ? { htmlAttrs: { class: 'home-bg' } } : {},
  ),
);

const { isAuthenticated } = storeToRefs(useAuthStore());
const { openRegister } = useAuth();
const modalStore = useModalStore();
const storiesStore = useStoriesStore();
const { getSecureQueryParam } = useRoutes();
const { isBetByEnabled, isCactusSportsbookEnabled } = useFeatureFlagsEnv();

const { isMaintenanceModeEnabled, isSiteShutdown } = useFeatureFlagsNew();

// Desligamento geral (Operações do site, ENG-915): a landing entra no lugar do
// `NuxtLayout`, como a parede do PWA logo abaixo. Virou com o site aberto,
// recarrega: o boot já sai certo dos dois lados — desligado, nenhum script de
// terceiro (chat, CRM, jogos) entra por cima da landing; religado, todos entram.
// `post`: recarrega só depois de o store persistir o status novo, pra que um
// boot que não alcance o core caia nele, e não no status de antes da virada.
watch(isSiteShutdown, () => window.location.reload(), { flush: 'post' });

// Parede de fim do PWA (ENG-843). Fica aqui, e não dentro de um layout, porque
// este é o único ponto acima do `NuxtLayout` — só assim o layout inteiro deixa
// de montar, nenhum middleware de rota roda e nada do site renderiza por trás.
const { shouldShowWall } = usePwaShutdown();

const { isZenDeskActive } = useLiveSupport();
const { isSportsPage } = useSportsBook();
const { appearance } = storeToRefs(useAppearanceStore());
const { startSmartico } = useSmarticoInit();
// Deep-links do Club Fidelidade (?open=levels / rewards / loja / post). Mora
// aqui pelo mesmo motivo do `story`: é o único ponto global.
const { start: startLoyaltyHubDeeplink } = useLoyaltyHubDeeplink();
const { loadStories } = useStories();
const { isSmarticoActived } = storeToRefs(useSmarticoConfigs());

const betByLoaded = ref(true);
const betByCss = ref('');

const customCss = computed<string>(() => {
  const cssCode = appearance.value.css_code;

  if (!cssCode || cssCode === '') {
    return '';
  }

  const parsedCss = cssCode.replace(/\\n/g, '').replace(/[\\"]/g, '');

  return `
      <style>
      ${parsedCss}
      </style>
        `;
});

const attempts = ref(0);

const checkBetBy = () => {
  if (process.client) {
    if (document !== undefined) {
      if (document.getElementById('betby')) {
        betByLoaded.value = true;
      } else {
        attempts.value++;
        if (attempts.value < 20) {
          setTimeout(checkBetBy, 100);
        }
      }
    }
  }
};

function customizeZenDesk() {
  if (!import.meta.client || !isZenDeskActive.value) {
    return;
  }

  if (!window.zE) {
    return;
  }

  const colors = {
    primary: 'ffffff',
    secondary: 'f0f0f0',
    texts: '1a1a1a',
    bubble: 'd0eaff',
    bgButton: '0096FF',
    textButton: 'ffffff',
  };

  window.zE('messenger', 'hide');

  window.zE('messenger:on', 'close', function () {
    window.zE('messenger', 'hide');
    console.debug('💬 Close Support Chat Widget');
    zenDesktStyle({ colors, remove: true, enabled: false });
  });

  window.zE('messenger:on', 'open', function () {
    console.debug('💬 Open Support Chat Widget');
    zenDesktStyle({ colors, remove: false, enabled: false });
  });
}

watch(
  () => route.path,
  () => {
    if (isSportsPage.value) {
      if (isBetByEnabled.value) {
        checkBetBy();
      }

      if (isCactusSportsbookEnabled.value) {
        const newStyle = `
          <style>
            body { overflow: hidden !important; }
          </style>
        `;

        betByCss.value = newStyle;
      }
    } else {
      betByLoaded.value = false;

      const newStyle = `
      <style>
        html { overflow-x: clip; }
        body { overflow-x: clip; }
      </style>
    `;

      betByCss.value = newStyle;
    }
  },
  { immediate: true },
);

watch(betByLoaded, (val) => {
  if (val) {
    betByCss.value = '';
  }
});

function start() {
  verifyInstanceOnWindow([
    {
      key: 'zE',
      onLoad: customizeZenDesk,
      condition: !!isZenDeskActive.value,
    },
    {
      key: '_smartico',
      onLoad: startSmartico,
      condition: !!isSmarticoActived.value,
    },
  ]);
}

function applyIframeStyles(
  iframe: HTMLIFrameElement,
  styles: Partial<CSSStyleDeclaration>,
) {
  Object.entries(styles).forEach(([key, value]) => {
    if (value && key in iframe.style) {
      iframe.style[key as any] = String(value);
    }
  });
}

onMounted(() => {
  if (isSiteShutdown.value) return;

  start();
  startLoyaltyHubDeeplink();
  loadStories();

  const applyStyles = () => {
    // ShortInfoWidget
    const shortInfoIframe = document.querySelector(
      'iframe[src="https://libs.s.cactusgaming.net/gf/ShortInfoWidget2.html?bridgeId=GamificationWidget"]',
    );

    if (shortInfoIframe instanceof HTMLIFrameElement) {
      applyIframeStyles(shortInfoIframe, {
        top: '54px',
        bottom: '0px',
      });
    }
  };

  applyStyles();

  const observer = new MutationObserver(() => applyStyles());

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
});

onActivated(() => {
  if (isSiteShutdown.value) return;

  start();
});

const STORY_QUERY_VALUE = 'story';

const pendingOpenStory = ref(false);
const { hasStories } = storeToRefs(storiesStore);

const isStoryOpen = computed(
  () => modalStore.isOpen && modalStore.component === ApStories,
);

function tryOpenStory() {
  if (hasStories.value && !isStoryOpen.value) {
    modalStore.open(ApStories);
    pendingOpenStory.value = false;
  }
}

onBeforeMount(() => {
  try {
    if (
      new URL(location.href).hostname !== new URL(document.referrer).hostname
    ) {
      if (document.referrer) {
        sessionStorage.setItem('c_documentReferrer', document.referrer);
        localStorage.setItem('c_documentReferrerLocal', document.referrer);
      }
    }
  } catch (ex) {}

  if (route.query.register === 'true' && !isAuthenticated.value) {
    const queryRegister = getSecureQueryParam(route.query.register);

    localStorage.setItem('register_data', JSON.stringify(queryRegister));

    openRegister();
  }

  if (route.query.open === STORY_QUERY_VALUE) {
    pendingOpenStory.value = true;
  }
});

const router = useRouter();

watch([pendingOpenStory, hasStories], ([pending]) => {
  if (pending) tryOpenStory();
});

router.afterEach((to) => {
  if (to.query.open === STORY_QUERY_VALUE && !isStoryOpen.value) {
    pendingOpenStory.value = true;
  }
});

watch(isStoryOpen, (open) => {
  if (!open && route.query.open === STORY_QUERY_VALUE) {
    const query = { ...route.query };

    delete query.open;
    router.replace({ query });
  }
});
</script>

<template>
  <ClientOnly>
    <BaseMaintenance v-if="isMaintenanceModeEnabled" />

    <!-- A parede fica FORA da cadeia do BaseMaintenance de propósito: encadear
         os dois mudaria o modo manutenção, que hoje renderiza o layout por
         baixo do overlay. Se ambos estiverem ativos, o manutenção tem z-index
         maior e cobre a parede, que é a precedência certa. -->
    <!-- O desligamento geral vence a parede do PWA: com o site desligado, nem
         o app oficial para onde ela manda tem o que abrir. -->
    <SiteShutdownLanding v-if="isSiteShutdown" />

    <PwaShutdownWall v-else-if="shouldShowWall" />

    <template v-else>
      <LazyAuthChallenge />

      <LazyBaseCommonToaster />

      <LazyPromocodeRedeemNotification />

      <NuxtLayout>
        <NuxtPage />
      </NuxtLayout>

      <div v-html="betByCss"></div>

      <div
        id="customCss"
        v-html="customCss"
      ></div>

      <LazyWidgetsBodyInjectedScript />

      <LazyApModalRenderer />

      <LazyAuthBiometricOptInPrompt />

      <LazyAuthBiometricColdStartGate />

      <LazyLevelsLevelUpWatcher />
    </template>
  </ClientOnly>
</template>

<style lang="scss">
body {
  &::-webkit-scrollbar {
    display: none;
  }

  @media print {
    background-color: #fff;
  }
}

.octa-widget-v2.octachatAppClose {
  bottom: calc(0.5rem + var(--bottomMenuHeight, 0px)) !important;
  right: 0.5rem !important;
}
</style>
